import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchMe } from '@/api/auth';
import { isTokenExpired } from '@/lib/jwt';
import { queryKeys } from '@/lib/query-keys';
import { selectToken, useAuthStore } from '@/stores/auth-store';

/**
 * `GET /api/auth/me` como query de TanStack Query.
 *
 * Es la única fuente que refresca usuario/tenants: la guarda `RequireAuth` la usa como
 * puerta de entrada al panel y, cuando responde, sincroniza el store (por si el usuario
 * cambió de rol o lo agregaron a otro negocio desde otro dispositivo).
 *
 * Se desactiva sola si no hay token o si ya expiró (evita un 401 seguro y un redirect
 * innecesario: la guarda ya manda a /login en ese caso).
 */
export function useMe() {
  const token = useAuthStore(selectToken);
  const enabled = Boolean(token) && !isTokenExpired(token);

  const query = useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: fetchMe,
    enabled,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!query.data) return;

    const activeToken = useAuthStore.getState().token;
    if (!activeToken) return;

    useAuthStore.getState().setSession({
      token: activeToken,
      user: query.data.user,
      tenants: query.data.tenants,
    });
  }, [query.data]);

  return query;
}
